import { useMemo, useState } from 'react';
import { Button, Drawer, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { Pencil, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { translationsApi } from '@api/translations';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useActiveLanguages } from '@features/languages/useLanguages';
import { LanguageCoverageTags } from './LanguageCoverageTags';
import { NamespaceRenameModal } from './NamespaceRenameModal';
import type { NamespaceSummary } from '../types';

const { Text } = Typography;

interface Props {
  open: boolean;
  namespaces: NamespaceSummary[];
  loading?: boolean;
  onClose: () => void;
}

export function NamespaceManagerDrawer({ open, namespaces, loading, onClose }: Props) {
  const { t } = useTranslation(['translations', 'common', 'errors']);
  const qc = useQueryClient();
  const { data: languages = [] } = useActiveLanguages();
  const activeCodes = useMemo(() => languages.map((l) => l.code), [languages]);
  const [renameTarget, setRenameTarget] = useState<NamespaceSummary | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (name: string) => translationsApi.deleteNamespace(name),
    onSuccess: (result) => {
      toast.success(
        t('namespaceManager.toast.deleted', {
          namespace: result.namespace,
          count: result.affectedRows,
        }),
      );
      void qc.invalidateQueries({ queryKey: ['translations'] });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const columns = [
    {
      key: 'namespace',
      title: t('namespaceManager.columns.namespace'),
      dataIndex: 'namespace',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      key: 'keyCount',
      title: t('namespaceManager.columns.keyCount'),
      dataIndex: 'keyCount',
      width: 90,
      render: (v: number) => <Tag>{v}</Tag>,
    },
    {
      key: 'languages',
      title: t('namespaceManager.columns.languages'),
      width: 160,
      render: (_: unknown, row: NamespaceSummary) => (
        <LanguageCoverageTags present={row.languageCodes} active={activeCodes} />
      ),
    },
    {
      key: 'actions',
      title: t('namespaceManager.columns.actions'),
      width: 96,
      render: (_: unknown, row: NamespaceSummary) => (
        <Space>
          <Tooltip title={t('namespaceManager.rename')}>
            <Button
              size="small"
              icon={<Pencil size={14} />}
              aria-label={t('namespaceManager.renameAria', { namespace: row.namespace })}
              onClick={() => setRenameTarget(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t('namespaceManager.deleteConfirmTitle')}
            description={t('namespaceManager.deleteConfirmDescription', {
              namespace: row.namespace,
              count: row.keyCount,
            })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(row.namespace)}
          >
            <Tooltip title={t('namespaceManager.delete')}>
              <Button
                size="small"
                danger
                icon={<Trash2 size={14} />}
                aria-label={t('namespaceManager.deleteAria', { namespace: row.namespace })}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      title={t('namespaceManager.title')}
      open={open}
      onClose={onClose}
      width={Math.min(640, typeof window !== 'undefined' ? window.innerWidth : 640)}
    >
      <p style={{ marginTop: 0, color: 'var(--ds-text-secondary)' }}>
        {t('namespaceManager.description')}
      </p>
      <Table<NamespaceSummary>
        rowKey="namespace"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={namespaces}
        pagination={false}
        locale={{ emptyText: <Empty description={t('namespaceManager.empty')} /> }}
      />
      <NamespaceRenameModal
        open={!!renameTarget}
        namespace={renameTarget}
        onClose={() => setRenameTarget(null)}
      />
    </Drawer>
  );
}
